import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import Map from 'ol/Map'
import Overlay from 'ol/Overlay'
import View from 'ol/View'
import Feature from 'ol/Feature'
import GeoJSON from 'ol/format/GeoJSON'
import { Circle as CircleGeometry, Point } from 'ol/geom'
import TileLayer from 'ol/layer/Tile'
import VectorLayer from 'ol/layer/Vector'
import XYZ from 'ol/source/XYZ'
import VectorSource from 'ol/source/Vector'
import { defaults as defaultControls } from 'ol/control'
import { fromLonLat, toLonLat } from 'ol/proj'
import { Circle as CircleStyle, Fill, RegularShape, Stroke, Style, Text } from 'ol/style'
import 'ol/ol.css'
import {
  ArrowDownTrayIcon,
  BuildingOffice2Icon,
  MapPinIcon,
  SparklesIcon,
  GlobeAsiaAustraliaIcon,
  ExclamationTriangleIcon,
  PrinterIcon
} from '@heroicons/react/24/outline'
import { useRecycling } from '../contexts/RecyclingContext'
import { districtAliasMap, districtPlanningStats, govRecyclingStations, type DistrictPlanningStat } from '../data/planningData'
import { rvmMachines } from '../data/rvmMachines'

type PlanningWindow = Window & { __PLANNING_API_BASE__?: string }

const resolvePlanningApiBase = () => {
  if (typeof window === 'undefined') return ''

  const explicitBase = ((window as PlanningWindow).__PLANNING_API_BASE__ || '').replace(/\/$/, '')
  if (explicitBase) return explicitBase

  if (window.location.port === '3000') {
    return 'http://localhost:4002'
  }

  return ''
}

const PLANNING_API_BASE = resolvePlanningApiBase()

const fetchJson = async <T,>(url: string): Promise<T> => {
  const response = await fetch(url)
  const contentType = response.headers.get('content-type') || ''
  const bodyText = await response.text()

  if (!response.ok) {
    throw new Error(`Request failed (${response.status})`)
  }

  if (!contentType.toLowerCase().includes('application/json')) {
    throw new Error(`Expected JSON response from ${url}, received ${contentType || 'unknown content type'}`)
  }

  return JSON.parse(bodyText) as T
}

const PROJECTION_YEARS = [2023, 2024, 2025, 2026, 2027, 2028, 2029, 2030, 2031] as const

type ProjectionYear = (typeof PROJECTION_YEARS)[number]

type PlanningYear = 2021 | ProjectionYear

interface LayerState {
  boundaries: boolean
  deficiency: boolean
  rvm: boolean
  govStations: boolean
  population: boolean
  projection: boolean
  coverage: boolean
}

interface CandidateSite {
  id: string
  latitude: number
  longitude: number
  nearestRvmMeters: number
  nearbyRvmCount: number
  nearbyGovStationCount: number
  district: string
  populationWeight: number
  score: number
  feasible: boolean
  invalidReason?: string
  createdAt: string
}

interface PlanningStation {
  id: string
  name: string
  district: string
  latitude: number
  longitude: number
  source?: string
}

interface DeficiencyRow {
  district: string
  center: [number, number]
  currentPopulation: number
  projectedPopulation: number
  rvmCount: number
  nearestDistance: number
  gapFactor: number
  score: number
  sduFactor: number
  rank?: number
  trendSeries?: Array<{ year: ProjectionYear; population: number; score: number }>
  trendDelta?: number
  changePercent?: number
  blockchainPlasticKg?: number
  blockchainCredits?: number
}

interface DeficiencyApiResponse {
  planningYear: PlanningYear
  generatedAt: string
  machineCount: number
  coverage: {
    percentage: number
    servedPopulation: number
    totalPopulation: number
    target: number
  }
  ranking: DeficiencyRow[]
}

interface RvmPlanningPageProps {
  readonlyView?: boolean
  homeRoute: string
  title?: string
}

interface MachineWithDistrict {
  machineId: string
  name: string
  address: string
  status: string
  latitude: number
  longitude: number
  district: string
}

interface DistrictPopulationStat extends DistrictPlanningStat {
  projectionsByYear?: Partial<Record<string, number>>
}

interface PopulationApiResponse {
  source: 'csdi' | 'fallback'
  fetchedAt: string
  warning?: string
  projectionWarning?: string
  count: number
  datasets: {
    current: {
      datasetId: string
      name: string
      serviceType: string
      layerName: string
    }
    projections: {
      datasetId: string
      name: string
      serviceType: string
      layerName: string
    }
  }
  districts: DistrictPopulationStat[]
}

interface DistrictBoundaryApiResponse {
  source: 'csdi' | 'fallback'
  fetchedAt: string
  warning?: string
  count: number
  geojson: {
    type: 'FeatureCollection'
    features: Array<Record<string, any>>
  }
}

const toRadians = (value: number) => (value * Math.PI) / 180

const distanceMeters = (a: [number, number], b: [number, number]) => {
  const R = 6371e3
  const lat1 = toRadians(a[0])
  const lat2 = toRadians(b[0])
  const dLat = toRadians(b[0] - a[0])
  const dLng = toRadians(b[1] - a[1])

  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2)

  return 2 * R * Math.asin(Math.sqrt(h))
}

const clamp = (value: number, min = 0, max = 100) => Math.max(min, Math.min(max, value))

const inferDistrictFromAddress = (address: string) => {
  for (const entry of districtAliasMap) {
    if (entry.keywords.some((keyword) => address.includes(keyword))) {
      return entry.district
    }
  }
  return 'Unknown'
}

const hashScore = (input: string) => {
  let hash = 0
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash)
}

const waterExclusionZones: Array<{ id: string; name: string; polygon: Array<[number, number]> }> = [
  {
    id: 'victoria-harbour',
    name: 'Victoria Harbour',
    polygon: [
      [22.285, 114.135],
      [22.300, 114.145],
      [22.314, 114.205],
      [22.303, 114.240],
      [22.281, 114.228],
      [22.272, 114.165]
    ]
  },
  {
    id: 'junk-bay',
    name: 'Junk Bay',
    polygon: [
      [22.293, 114.255],
      [22.320, 114.253],
      [22.327, 114.285],
      [22.309, 114.302],
      [22.289, 114.289],
      [22.286, 114.267]
    ]
  },
  {
    id: 'tolo-harbour',
    name: 'Tolo Harbour',
    polygon: [
      [22.404, 114.176],
      [22.449, 114.180],
      [22.476, 114.236],
      [22.450, 114.272],
      [22.409, 114.252],
      [22.392, 114.206]
    ]
  }
]

const isPointInPolygon = (point: [number, number], polygon: Array<[number, number]>) => {
  const [lat, lng] = point
  let isInside = false

  for (let currentIndex = 0, previousIndex = polygon.length - 1; currentIndex < polygon.length; previousIndex = currentIndex++) {
    const [currentLat, currentLng] = polygon[currentIndex]
    const [previousLat, previousLng] = polygon[previousIndex]

    const intersects =
      currentLng > lng !== previousLng > lng &&
      lat < ((previousLat - currentLat) * (lng - currentLng)) / (previousLng - currentLng) + currentLat

    if (intersects) {
      isInside = !isInside
    }
  }

  return isInside
}

const interpolate = (start: number, end: number, factor: number) => start + (end - start) * factor

const getProjectedPopulationForYear = (district: DistrictPopulationStat, year: PlanningYear) => {
  if (district.projectionsByYear && district.projectionsByYear[String(year)] !== undefined) {
    return Number(district.projectionsByYear[String(year)]) || 0
  }

  if (year <= 2021) {
    return district.population2021
  }

  if (year <= 2027) {
    return Math.round(interpolate(district.population2021, district.projectedPopulation2027, (year - 2021) / 6))
  }

  return Math.round(interpolate(district.projectedPopulation2027, district.projectedPopulation2031, (year - 2027) / 4))
}

const getDeficiencyScoreForPopulation = (population: number, rvmCount: number, nearestDistance: number, sduFactor: number) => {
  const gapFactor = nearestDistance > 1000 ? 1.25 : 1
  return (population / Math.max(rvmCount, 1)) * (1 + sduFactor) * gapFactor
}

const getDistrictBoundaryName = (properties: Record<string, any>) => {
  return String(
    properties.NAME_EN ||
      properties.NAME_TC ||
      properties.name ||
      properties.district ||
      properties.DISTRICT ||
      'Unknown'
  )
}

const getTrendArrow = (delta: number) => {
  if (delta > 0) return '↑'
  if (delta < 0) return '↓'
  return '→'
}

const getSparklinePoints = (series: Array<{ year: ProjectionYear; score: number }>, width = 120, height = 28) => {
  if (series.length === 0) {
    return { path: '', points: '', scaledPoints: [] as Array<{ x: number; y: number }> }
  }

  const minScore = Math.min(...series.map((item) => item.score))
  const maxScore = Math.max(...series.map((item) => item.score))
  const scoreRange = Math.max(1, maxScore - minScore)
  const stepX = series.length > 1 ? width / (series.length - 1) : width

  const scaledPoints = series.map((item, index) => {
    const x = index * stepX
    const normalized = (item.score - minScore) / scoreRange
    const y = height - normalized * height
    return { x, y }
  })

  const path = scaledPoints.map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x.toFixed(2)},${point.y.toFixed(2)}`).join(' ')
  const points = scaledPoints.map((point) => `${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(' ')

  return { path, points, scaledPoints }
}

const createFallbackDistrictBoundaryGeojson = () => {
  return {
    type: 'FeatureCollection' as const,
    features: districtPlanningStats.map((district, index) => {
      const populationWeight = Math.max(district.population2021, district.projectedPopulation2031) / 1000000
      const radiusLng = Math.min(0.035, Math.max(0.012, 0.012 + populationWeight * 0.012))
      const radiusLat = Math.min(0.03, Math.max(0.01, 0.01 + populationWeight * 0.009))
      const steps = 8
      const coordinates: Array<[number, number]> = []

      for (let pointIndex = 0; pointIndex < steps; pointIndex += 1) {
        const angle = (index / districtPlanningStats.length) * Math.PI + (pointIndex / steps) * Math.PI * 2
        const wobble = pointIndex % 2 === 0 ? 1 : 0.82
        coordinates.push([
          district.center[1] + Math.cos(angle) * radiusLng * wobble,
          district.center[0] + Math.sin(angle) * radiusLat * wobble
        ])
      }

      coordinates.push(coordinates[0])

      return {
        type: 'Feature',
        id: `fallback-district-${index}`,
        properties: {
          OBJECTID: index + 1,
          AREA_ID: district.district.replace(/\s+/g, '-').toUpperCase(),
          AREA_TYPE: 'District',
          CSDI_ADMIN_AREA_ID: district.district.replace(/\s+/g, '-').toUpperCase(),
          NAME_EN: district.district,
          NAME_TC: district.district,
          DATA_OWNER: 'Fallback',
          GmlID: `fallback-${index}`,
          SHAPE_Length: 0
        },
        geometry: {
          type: 'Polygon',
          coordinates: [coordinates]
        }
      }
    })
  }
}

interface CSDIPlanningMapProps {
  layerState: LayerState
  machineWithDistrict: MachineWithDistrict[]
  effectiveGovStations: PlanningStation[]
  planningYear: PlanningYear
  utilizationLookup: Record<string, { monthlyUnits: number; lowUtilization: boolean; highDemand: boolean }>
  districtMetrics: DeficiencyRow[]
  districtPopulationStats: DistrictPopulationStat[]
  candidateSites: CandidateSite[]
  onBoundarySourceChange?: (source: 'csdi' | 'fallback') => void
  onBoundaryWarningChange?: (warning: string | null) => void
  onMapClick: (lat: number, lng: number) => void
}

const CSDI_TOPO_URL = 'https://mapapi.geodata.gov.hk/gs/api/v1.0.0/xyz/basemap/wgs84/{z}/{x}/{y}.png'

const CSDIPlanningMap: React.FC<CSDIPlanningMapProps> = ({
  layerState,
  machineWithDistrict,
  effectiveGovStations,
  planningYear,
  utilizationLookup,
  districtMetrics,
  districtPopulationStats,
  candidateSites,
  onBoundarySourceChange,
  onBoundaryWarningChange,
  onMapClick
}) => {
  const mapElementRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<Map | null>(null)
  const interactiveSourceRef = useRef<VectorSource | null>(null)
  const districtBoundarySourceRef = useRef<VectorSource | null>(null)
  const districtBoundaryLayerRef = useRef<VectorLayer<VectorSource> | null>(null)
  const districtBoundaryGeojsonRef = useRef<DistrictBoundaryApiResponse['geojson'] | null>(null)
  const districtMetricsLookupRef = useRef<Record<string, DeficiencyRow>>({})
  const layerStateRef = useRef(layerState)
  const planningYearRef = useRef(planningYear)
  
  const onMapClickRef = useRef(onMapClick)
  useEffect(() => {
    onMapClickRef.current = onMapClick
  }, [onMapClick])

  useEffect(() => {
    layerStateRef.current = layerState
    districtBoundaryLayerRef.current?.changed()
  }, [layerState])

  useEffect(() => {
    planningYearRef.current = planningYear
  }, [planningYear])

  useEffect(() => {
    const boundaryVisible = layerState.boundaries || layerState.deficiency
    districtBoundaryLayerRef.current?.setVisible(boundaryVisible)
  }, [layerState.boundaries, layerState.deficiency])

  useEffect(() => {
    districtMetricsLookupRef.current = districtMetrics.reduce<Record<string, DeficiencyRow>>((accumulator, district) => {
      accumulator[district.district] = district
      return accumulator
    }, {})
    districtBoundaryLayerRef.current?.changed()
  }, [districtMetrics])
  
  const popupRef = useRef<HTMLDivElement | null>(null)
  const popupOverlayRef = useRef<Overlay | null>(null)
  const [popupContent, setPopupContent] = useState<{ type: string; data: any; coordinate: number[] } | null>(null)
  const [districtBoundarySource, setDistrictBoundarySource] = useState<'csdi' | 'fallback'>('fallback')
  const [districtBoundaryWarning, setDistrictBoundaryWarning] = useState<string | null>(null)

  useEffect(() => {
    onBoundarySourceChange?.(districtBoundarySource)
  }, [districtBoundarySource, onBoundarySourceChange])

  useEffect(() => {
    onBoundaryWarningChange?.(districtBoundaryWarning)
  }, [districtBoundaryWarning, onBoundaryWarningChange])

  const applyDistrictBoundaryFeatures = () => {
    const source = districtBoundarySourceRef.current
    const geojson = districtBoundaryGeojsonRef.current

    if (!source || !geojson) return

    const features = new GeoJSON().readFeatures(geojson as any, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857'
    })

    features.forEach((feature) => {
      const properties = feature.getProperties() as Record<string, any>
      const districtName = getDistrictBoundaryName(properties)
      feature.setProperties(
        {
          type: 'districtBoundary',
          districtKey: districtName,
          data: { district: districtName, NAME_EN: properties.NAME_EN, NAME_TC: properties.NAME_TC }
        },
        true
      )
    })

    source.clear()
    source.addFeatures(features)
    districtBoundaryLayerRef.current?.changed()
  }

  useEffect(() => {
    let cancelled = false

    const loadDistrictBoundaries = async () => {
      try {
        setDistrictBoundaryWarning(null)
        const response = await fetchJson<DistrictBoundaryApiResponse>(`${PLANNING_API_BASE}/api/planning/district-boundaries`)
        if (cancelled) return

        districtBoundaryGeojsonRef.current = response.geojson
        setDistrictBoundarySource(response.source)
        setDistrictBoundaryWarning(response.warning || null)
        applyDistrictBoundaryFeatures()
      } catch (error) {
        if (cancelled) return

        districtBoundaryGeojsonRef.current = createFallbackDistrictBoundaryGeojson()
        setDistrictBoundarySource('fallback')
        setDistrictBoundaryWarning(error instanceof Error ? error.message : 'Failed to load district boundaries')
        applyDistrictBoundaryFeatures()
      }
    }

    void loadDistrictBoundaries()

    return () => {
      cancelled = true
    }
  }, [])

  // Show popup automatically for the most recently added candidate site
  const prevCandidatesCount = useRef(candidateSites.length)
  useEffect(() => {
    if (candidateSites.length > prevCandidatesCount.current) {
      const newSite = candidateSites[candidateSites.length - 1]
      const coord = fromLonLat([newSite.longitude, newSite.latitude])
      setPopupContent({ type: 'candidate', data: newSite, coordinate: coord })
      popupOverlayRef.current?.setPosition(coord)
    }
    prevCandidatesCount.current = candidateSites.length
  }, [candidateSites])

  useEffect(() => {
    if (!mapElementRef.current || mapRef.current) return

    const tileLayer = new TileLayer({
      source: new XYZ({
        url: CSDI_TOPO_URL,
        minZoom: 10,
        maxZoom: 20,
        crossOrigin: 'anonymous'
      })
    })

    const boundarySource = new VectorSource()
    const boundaryLayer = new VectorLayer({
      source: boundarySource,
      declutter: true,
      style: (feature) => {
        const properties = feature.getProperties() as Record<string, any>
        const districtName = String(properties.districtKey || getDistrictBoundaryName(properties))
        const metric = districtMetricsLookupRef.current[districtName]
        const allScores = Object.values(districtMetricsLookupRef.current).map((item) => item.score)
        const minScore = allScores.length > 0 ? Math.min(...allScores) : 0
        const maxScore = allScores.length > 0 ? Math.max(...allScores) : 1
        const scoreRange = Math.max(1, maxScore - minScore)
        const normalizedScore = metric ? (metric.score - minScore) / scoreRange : 0
        const hue = 120 - normalizedScore * 120
        const fillColor = layerStateRef.current.deficiency
          ? `hsla(${hue.toFixed(1)}, 78%, 52%, 0.28)`
          : 'rgba(255,255,255,0.08)'
        const strokeColor = metric
          ? 'rgba(17,24,39,0.92)'
          : 'rgba(107,114,128,0.7)'

        return new Style({
          fill: new Fill({ color: fillColor }),
          stroke: new Stroke({ color: strokeColor, width: 1.4 }),
          text: new Text({
            text: districtName,
            font: '12px sans-serif',
            overflow: true,
            placement: 'point',
            fill: new Fill({ color: '#111827' }),
            stroke: new Stroke({ color: 'rgba(255,255,255,0.95)', width: 3 })
          })
        })
      }
    })
    districtBoundarySourceRef.current = boundarySource
    districtBoundaryLayerRef.current = boundaryLayer

    const interactiveSource = new VectorSource()
    const interactiveLayer = new VectorLayer({ source: interactiveSource })
    interactiveSourceRef.current = interactiveSource

    const map = new Map({
      target: mapElementRef.current,
      controls: defaultControls({ attribution: false }),
      layers: [tileLayer, boundaryLayer, interactiveLayer],
      view: new View({
        center: fromLonLat([114.17, 22.34]),
        zoom: 11,
        minZoom: 10,
        maxZoom: 20
      })
    })

    if (popupRef.current) {
      const overlay = new Overlay({
        element: popupRef.current,
        positioning: 'bottom-center',
        stopEvent: false,
        offset: [0, -15],
      })
      map.addOverlay(overlay)
      popupOverlayRef.current = overlay
    }

    applyDistrictBoundaryFeatures()

    map.on('singleclick', (event) => {
      const feature = map.forEachFeatureAtPixel(event.pixel, (feat) => feat)
      if (feature) {
        const props = feature.getProperties()
        if (props.type === 'districtBoundary') {
          const districtName = String(props.districtKey || props.data?.district || '')
          const metric = districtMetricsLookupRef.current[districtName]
          const districtStat = districtPopulationStats.find((item) => item.district === districtName)

          if (metric && districtStat) {
            setPopupContent({
              type: props.type,
              data: {
                district: districtName,
                currentPopulation: districtStat.population2021,
                projectedPopulation: metric.projectedPopulation,
                changePercent: metric.changePercent ?? 0,
                rvmCount: metric.rvmCount,
                score: metric.score,
                rank: metric.rank ?? 0,
                planningYear: planningYearRef.current
              },
              coordinate: event.coordinate
            })
            popupOverlayRef.current?.setPosition(event.coordinate)
            return
          }
        }

        if (props.type && props.data) {
          setPopupContent({ type: props.type, data: props.data, coordinate: event.coordinate })
          popupOverlayRef.current?.setPosition(event.coordinate)
          return
        }
      }
      
      // If clicking empty map
      setPopupContent(null)
      popupOverlayRef.current?.setPosition(undefined)
      
      const [lng, lat] = toLonLat(event.coordinate)
      onMapClickRef.current(lat, lng)
    })

    map.on('pointermove', (e) => {
      const pixel = map.getEventPixel(e.originalEvent)
      const hit = map.hasFeatureAtPixel(pixel)
      map.getTargetElement().style.cursor = hit ? 'pointer' : ''
    })

    mapRef.current = map

    const mapTarget = mapElementRef.current
    const resizeObserver = typeof ResizeObserver !== 'undefined' && mapTarget
      ? new ResizeObserver(() => {
          map.updateSize()
        })
      : null

    if (resizeObserver && mapTarget) {
      resizeObserver.observe(mapTarget)
    }

    map.updateSize()

    return () => {
      resizeObserver?.disconnect()
      map.setTarget(undefined)
      mapRef.current = null
      interactiveSourceRef.current = null
      districtBoundarySourceRef.current = null
      districtBoundaryLayerRef.current = null
    }
  }, [])

  useEffect(() => {
    const source = interactiveSourceRef.current
    if (!source) return

    source.clear()

    const pointStyle = (radius: number, strokeColor: string, fillColor: string, label?: string, labelColor = '#111827', zIndex = 0) =>
      new Style({
        zIndex,
        image: new CircleStyle({
          radius,
          stroke: new Stroke({ color: strokeColor, width: 1.2 }),
          fill: new Fill({ color: fillColor })
        }),
        text: label
          ? new Text({
              text: label,
              font: '11px sans-serif',
              fill: new Fill({ color: labelColor }),
              stroke: new Stroke({ color: 'rgba(255,255,255,0.9)', width: 3 }),
              overflow: true,
              offsetY: 14
            })
          : undefined
      })

    const diamondStyle = (strokeColor: string, fillColor: string, label?: string, zIndex = 0) =>
      new Style({
        zIndex,
        image: new RegularShape({
          points: 4,
          radius: 7,
          angle: Math.PI / 4,
          stroke: new Stroke({ color: strokeColor, width: 1.3 }),
          fill: new Fill({ color: fillColor })
        }),
        text: label
          ? new Text({
              text: label,
              font: '10px sans-serif',
              fill: new Fill({ color: '#111827' }),
              stroke: new Stroke({ color: 'rgba(255,255,255,0.9)', width: 3 }),
              offsetY: 16,
              overflow: true
            })
          : undefined
      })

    const ringStyle = (strokeColor: string, fillColor: string, width = 1, lineDash?: number[], zIndex = 0) =>
      new Style({
        zIndex,
        stroke: new Stroke({ color: strokeColor, width, lineDash }),
        fill: new Fill({ color: fillColor })
      })

    if (layerState.rvm) {
      machineWithDistrict.forEach((machine) => {
        const performance = utilizationLookup[machine.machineId]
        const strokeColor = performance?.highDemand ? '#991b1b' : performance?.lowUtilization ? '#b45309' : '#0f766e'
        const fillColor = performance?.highDemand
          ? 'rgba(220,38,38,0.88)'
          : performance?.lowUtilization
            ? 'rgba(245,158,11,0.88)'
            : 'rgba(20,184,166,0.88)'
        const feature = new Feature({ geometry: new Point(fromLonLat([machine.longitude, machine.latitude])) })
        feature.setProperties({ type: 'rvm', data: machine, utilization: performance })
        feature.setStyle(diamondStyle(strokeColor, fillColor, undefined, 50))
        source.addFeature(feature)
      })
    }

    if (layerState.coverage) {
      machineWithDistrict.filter((machine) => machine.status === 'open').forEach((machine) => {
        const feature = new Feature({ geometry: new CircleGeometry(fromLonLat([machine.longitude, machine.latitude]), 500) })
        feature.setStyle(ringStyle('#22c55e', 'rgba(34,197,94,0.08)', 1, undefined, 10))
        source.addFeature(feature)
      })
    }

    if (layerState.govStations) {
      effectiveGovStations.forEach((station) => {
        const feature = new Feature({ geometry: new Point(fromLonLat([station.longitude, station.latitude])) })
        feature.setProperties({ type: 'govStation', data: station })
        feature.setStyle(pointStyle(7, '#1d4ed8', 'rgba(59,130,246,0.78)', undefined, '#111827', 40))
        source.addFeature(feature)
      })
    }

    if (layerState.population) {
      districtPopulationStats.forEach((district) => {
        const densityScale = Math.max(8, Math.min(24, district.population2021 / 35000))
        const label = district.population2021.toLocaleString()
        const feature = new Feature({ geometry: new Point(fromLonLat([district.center[1], district.center[0]])) })
        feature.setProperties({ type: 'population', data: district })
        feature.setStyle(pointStyle(densityScale, '#d97706', 'rgba(250,204,21,0.82)', label, '#713f12', 30))
        source.addFeature(feature)
      })
    }

    if (layerState.projection) {
      districtPopulationStats.forEach((district) => {
        const projected = getProjectedPopulationForYear(district, planningYear)
        const feature = new Feature({ geometry: new Point(fromLonLat([district.center[1], district.center[0]])) })
        feature.setProperties({ type: 'projection', data: district })
        feature.setStyle(pointStyle(Math.max(7, Math.min(22, projected / 40000)), '#4b5563', 'rgba(156,163,175,0.54)', projected.toLocaleString(), '#374151', 20))
        source.addFeature(feature)
      })
    }

    candidateSites.forEach((site) => {
      const color = site.feasible ? '#7c3aed' : '#b91c1c'
      const fill = site.feasible ? 'rgba(139,92,246,0.85)' : 'rgba(239,68,68,0.85)'
      const feature = new Feature({ geometry: new Point(fromLonLat([site.longitude, site.latitude])) })
      feature.setProperties({ type: 'candidate', data: site })
      feature.setStyle(pointStyle(8, color, fill, site.feasible ? `S ${site.score}` : 'Rejected', '#111827', 60))
      source.addFeature(feature)
    })
  }, [layerState, machineWithDistrict, effectiveGovStations, planningYear, utilizationLookup, candidateSites, districtPopulationStats])

  return (
    <div className="relative h-full w-full">
      <div ref={mapElementRef} className="absolute inset-0" />
      
      {/* Map Popup Overlay */}
      <div 
        ref={popupRef} 
        className="absolute bg-white rounded-lg shadow-xl p-3 text-sm min-w-[240px] z-[500] pointer-events-auto"
        style={{ display: popupContent ? 'block' : 'none' }}
      >
        {popupContent && (
          <div>
            <div className="flex justify-between items-start mb-2 border-b pb-2 border-gray-100">
               <h4 className="font-bold text-gray-800 uppercase tracking-wider text-xs">
                 {popupContent.type === 'rvm' ? 'RVM Machine' : 
                  popupContent.type === 'govStation' ? 'Gov Station' : 
                  popupContent.type === 'candidate' ? 'Candidate Site' : 
                popupContent.type === 'districtBoundary' ? 'District Summary' : 
                popupContent.type === 'population' ? 'Population' : 
                popupContent.type === 'projection' ? 'Projected Population' : 
                  popupContent.type}
               </h4>
               <button 
                 onClick={() => { setPopupContent(null); popupOverlayRef.current?.setPosition(undefined) }} 
                 className="text-gray-400 hover:text-gray-600 rounded bg-gray-100 px-1 -mt-1 -mr-1"
               >&times;</button>
            </div>
            
            {popupContent.type === 'candidate' && (
              <div className="space-y-1 text-xs">
                <p><span className="text-gray-500 w-24 inline-block">Candidate ID:</span> {popupContent.data.id}</p>
                <p><span className="text-gray-500 w-24 inline-block">District:</span> {popupContent.data.district}</p>
                <p><span className="text-gray-500 w-24 inline-block">Status:</span> <span className={popupContent.data.feasible ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>{popupContent.data.feasible ? 'Feasible' : 'Not Recommended'}</span></p>
                <div className="pt-1 mt-1 border-t border-gray-50 border-dashed">
                  <p><span className="text-gray-500 w-24 inline-block">RVMs &le; 1km:</span> {popupContent.data.nearbyRvmCount}</p>
                  <p><span className="text-gray-500 w-24 inline-block">Stations &le; 1km:</span> {popupContent.data.nearbyGovStationCount}</p>
                  <p><span className="text-gray-500 w-24 inline-block">Dist. to RVM:</span> {(popupContent.data.nearestRvmMeters ?? 0).toFixed(0)}m</p>
                </div>
              </div>
            )}
            
            {popupContent.type === 'rvm' && (
              <div className="space-y-1 text-xs">
                <p><span className="text-gray-500 w-24 inline-block">Machine ID:</span> {popupContent.data.machineId}</p>
                <p className="flex"><span className="text-gray-500 w-24 shrink-0">Address:</span> <span className="flex-1">{popupContent.data.address}</span></p>
                <p><span className="text-gray-500 w-24 inline-block">Status:</span> <span className={popupContent.data.status === 'open' ? 'text-green-600 font-bold' : 'text-gray-500 font-bold'}>{popupContent.data.status}</span></p>
                {popupContent.data.utilization && (
                  <>
                    <p><span className="text-gray-500 w-24 inline-block">Monthly Units:</span> {popupContent.data.utilization.monthlyUnits}</p>
                    <p><span className="text-gray-500 w-24 inline-block">Utilisation:</span> <span className={popupContent.data.utilization.highDemand ? 'text-red-600 font-bold' : popupContent.data.utilization.lowUtilization ? 'text-amber-600 font-bold' : 'text-emerald-600 font-bold'}>{popupContent.data.utilization.highDemand ? 'High demand' : popupContent.data.utilization.lowUtilization ? 'Low utilisation' : 'Balanced'}</span></p>
                  </>
                )}
              </div>
            )}
            
            {popupContent.type === 'govStation' && (
              <div className="space-y-1 text-xs">
                <p className="flex"><span className="text-gray-500 w-24 shrink-0">Station Name:</span> <span className="flex-1">{popupContent.data.name}</span></p>
                <p><span className="text-gray-500 w-24 inline-block">Type:</span> {popupContent.data.type}</p>
              </div>
            )}

            {popupContent.type === 'districtBoundary' && (
              <div className="space-y-1 text-xs">
                <p><span className="text-gray-500 w-24 inline-block">District:</span> {popupContent.data.district}</p>
                <p><span className="text-gray-500 w-24 inline-block">Current Pop:</span> {popupContent.data.currentPopulation.toLocaleString()}</p>
                <p><span className="text-gray-500 w-24 inline-block">Projected Pop:</span> {popupContent.data.projectedPopulation.toLocaleString()}</p>
                <p><span className="text-gray-500 w-24 inline-block">Change:</span> {popupContent.data.changePercent.toFixed(1)}%</p>
                <p><span className="text-gray-500 w-24 inline-block">RVM Count:</span> {popupContent.data.rvmCount}</p>
                <p><span className="text-gray-500 w-24 inline-block">Score:</span> {popupContent.data.score.toFixed(0)}</p>
                <p><span className="text-gray-500 w-24 inline-block">Rank:</span> #{popupContent.data.rank}</p>
              </div>
            )}

            {popupContent.type === 'population' && (
              <div className="space-y-1 text-xs">
                <p><span className="text-gray-500 w-24 inline-block">District:</span> {popupContent.data.district}</p>
                <p><span className="text-gray-500 w-24 inline-block">Population:</span> {popupContent.data.population2021.toLocaleString()}</p>
              </div>
            )}

            {popupContent.type === 'projection' && (
              <div className="space-y-1 text-xs">
                <p><span className="text-gray-500 w-24 inline-block">District:</span> {popupContent.data.district}</p>
                <p><span className="text-gray-500 w-24 inline-block">Year:</span> {planningYearRef.current}</p>
                <p><span className="text-gray-500 w-24 inline-block">Projected:</span> {getProjectedPopulationForYear(popupContent.data, planningYearRef.current).toLocaleString()}</p>
              </div>
            )}
          </div>
        )}
      </div>

      <a
        href="https://api.portal.hkmapservice.gov.hk/disclaimer"
        target="_blank"
        rel="noreferrer"
        className="absolute bottom-2 right-12 z-[400] rounded bg-white/80 px-2 py-1 text-[11px] text-gray-800"
      >
        © Map information from Lands Department & CSDI 
      </a>
      <img
        src="https://api.hkmapservice.gov.hk/mapapi/landsdlogo.jpg"
        alt="LandsD logo"
        className="absolute bottom-2 right-2 z-[400] h-7 w-7 rounded bg-white/80 p-0.5"
      />
    </div>
  )
}

const RvmPlanningPage: React.FC<RvmPlanningPageProps> = ({ readonlyView = false, homeRoute, title = 'RVM Site Planning & Coverage Analysis' }) => {
  const [planningYear, setPlanningYear] = useState<PlanningYear>(2027)
  const [layerState, setLayerState] = useState<LayerState>({
    boundaries: true,
    deficiency: true,
    rvm: true,
    govStations: true,
    population: true,
    projection: true,
    coverage: true
  })
  const [candidateSites, setCandidateSites] = useState<CandidateSite[]>([])
  const [apiStations, setApiStations] = useState<PlanningStation[]>([])
  const [apiStationSource, setApiStationSource] = useState<'csdi' | 'fallback'>('fallback')
  const [apiStationWarning, setApiStationWarning] = useState<string | null>(null)
  const [districtBoundarySource, setDistrictBoundarySource] = useState<'csdi' | 'fallback'>('fallback')
  const [districtBoundaryWarning, setDistrictBoundaryWarning] = useState<string | null>(null)
  const [populationApiData, setPopulationApiData] = useState<PopulationApiResponse | null>(null)
  const [populationApiError, setPopulationApiError] = useState<string | null>(null)
  const [deficiencyFromApi, setDeficiencyFromApi] = useState<DeficiencyApiResponse | null>(null)
  const [csdiReferences, setCsdiReferences] = useState<Array<{ name: string; purpose: string; recommendedLayerType: string }>>([])
  const [apiError, setApiError] = useState<string | null>(null)

  const { getDistrictTotals } = useRecycling()
  const internalDistrictTotals = getDistrictTotals()

  useEffect(() => {
    let cancelled = false

    const loadPopulationData = async () => {
      try {
        setPopulationApiError(null)
        const populationJson = await fetchJson<PopulationApiResponse>(`${PLANNING_API_BASE}/api/planning/population`)
        if (cancelled) return
        setPopulationApiData(populationJson)
      } catch (error) {
        if (cancelled) return
        setPopulationApiError(error instanceof Error ? error.message : 'Failed to load population data')
        setPopulationApiData(null)
      }
    }

    void loadPopulationData()

    return () => {
      cancelled = true
    }
  }, [])

  const effectiveDistrictStats = useMemo<DistrictPopulationStat[]>(() => {
    if (populationApiData?.districts?.length) {
      return populationApiData.districts
    }
    return districtPlanningStats
  }, [populationApiData])

  useEffect(() => {
    if (import.meta.env.VITE_ENABLE_PLANNING_API !== 'true') {
      setApiError(null)
      setApiStationWarning(null)
      setDeficiencyFromApi(null)
      setApiStations([])
      setCsdiReferences([])
      return
    }

    let cancelled = false

    const loadPlanningApis = async () => {
      try {
        setApiError(null)

        const [stationJson, deficiencyJson, refsJson] = await Promise.all([
          fetchJson<{ stations: PlanningStation[]; source: 'csdi' | 'fallback'; warning?: string }>(`${PLANNING_API_BASE}/api/planning/epd-stations`),
          fetchJson<DeficiencyApiResponse>(`${PLANNING_API_BASE}/api/planning/deficiency?year=${planningYear}`),
          fetchJson<{ references: Array<{ name: string; purpose: string; recommendedLayerType: string }> }>(`${PLANNING_API_BASE}/api/planning/csdi-references`)
        ])

        if (cancelled) return

        setApiStations(Array.isArray(stationJson.stations) ? stationJson.stations : [])
        setApiStationSource(stationJson.source === 'csdi' ? 'csdi' : 'fallback')
        setApiStationWarning(stationJson.warning || null)
        setDeficiencyFromApi(deficiencyJson)
        setCsdiReferences(Array.isArray(refsJson.references) ? refsJson.references : [])
      } catch (error) {
        if (cancelled) return
        setApiError(error instanceof Error ? error.message : 'Failed to load planning APIs')
      }
    }

    void loadPlanningApis()

    return () => {
      cancelled = true
    }
  }, [planningYear])

  const machineWithDistrict = useMemo(
    () =>
      rvmMachines
        .filter((machine) => Number.isFinite(machine.latitude) && Number.isFinite(machine.longitude))
        .map((machine) => ({
          ...machine,
          district: inferDistrictFromAddress(machine.address)
        })),
    []
  )

  const districtRvmCount = useMemo(() => {
    const counter: Record<string, number> = {}
    machineWithDistrict.forEach((machine) => {
      const district = machine.district
      counter[district] = (counter[district] || 0) + 1
    })
    return counter
  }, [machineWithDistrict])

  const utilizationByMachine = useMemo(() => {
    return machineWithDistrict.map((machine) => {
      const baseline = 450 + (hashScore(machine.machineId) % 850)
      const demandMultiplier = machine.status === 'open' ? 1.12 : 0.58
      const monthlyUnits = Math.round(baseline * demandMultiplier)
      return {
        machineId: machine.machineId,
        monthlyUnits,
        lowUtilization: monthlyUnits < 550,
        highDemand: monthlyUnits > 1050
      }
    })
  }, [machineWithDistrict])

  const utilizationLookup = useMemo(() => {
    return utilizationByMachine.reduce<Record<string, { monthlyUnits: number; lowUtilization: boolean; highDemand: boolean }>>((acc, item) => {
      acc[item.machineId] = item
      return acc
    }, {})
  }, [utilizationByMachine])

  const effectiveGovStations = useMemo(() => {
    return apiStations.length > 0 ? apiStations : govRecyclingStations
  }, [apiStations])

  const deficiencyRanking = useMemo(() => {
    const baseRanking =
      deficiencyFromApi && Array.isArray(deficiencyFromApi.ranking) && deficiencyFromApi.ranking.length > 0
        ? deficiencyFromApi.ranking
        : effectiveDistrictStats.map((district) => {
            const rvmCount = districtRvmCount[district.district] || 0
            const projectedPopulation = getProjectedPopulationForYear(district, planningYear)
            const nearestDistance = Math.min(
              ...machineWithDistrict.map((machine) =>
                distanceMeters(district.center, [machine.latitude, machine.longitude])
              )
            )
            const gapFactor = nearestDistance > 1000 ? 1.25 : 1
            const score = getDeficiencyScoreForPopulation(projectedPopulation, rvmCount, nearestDistance, district.sduFactor)

            return {
              district: district.district,
              center: district.center,
              currentPopulation: district.population2021,
              projectedPopulation,
              rvmCount,
              nearestDistance,
              gapFactor,
              score,
              sduFactor: district.sduFactor
            }
          })

    return baseRanking
      .map((district, index) => {
        const districtStat = effectiveDistrictStats.find((item) => item.district === district.district)
        const internalTotal = internalDistrictTotals.find((item) => item.district === district.district)
        const currentPopulation = districtStat?.population2021 || district.currentPopulation || 0
        const projectedPopulation = districtStat ? getProjectedPopulationForYear(districtStat, planningYear) : district.projectedPopulation
        const rvmCount = district.rvmCount || districtRvmCount[district.district] || 0
        const nearestDistance = Number.isFinite(district.nearestDistance) ? district.nearestDistance : 999999
        const scoreTrend = districtStat
          ? PROJECTION_YEARS.map((year) => {
              const population = getProjectedPopulationForYear(districtStat, year)
              return {
                year,
                population,
                score: getDeficiencyScoreForPopulation(population, rvmCount, nearestDistance, districtStat.sduFactor)
              }
            })
          : []
        const selectedTrendIndex = scoreTrend.findIndex((entry) => entry.year === planningYear)
        const trendDelta = selectedTrendIndex > 0 ? scoreTrend[selectedTrendIndex].score - scoreTrend[selectedTrendIndex - 1].score : 0
        const changePercent = currentPopulation > 0 ? ((projectedPopulation - currentPopulation) / currentPopulation) * 100 : 0

        return {
          ...district,
          center: districtStat?.center || district.center,
          currentPopulation,
          projectedPopulation,
          rvmCount,
          nearestDistance,
          gapFactor: district.gapFactor || (nearestDistance > 1000 ? 1.25 : 1),
          score: district.score,
          sduFactor: districtStat?.sduFactor || district.sduFactor || 0,
          rank: index + 1,
          trendSeries: scoreTrend,
          trendDelta,
          changePercent,
          blockchainPlasticKg: internalTotal?.plasticKg || 0,
          blockchainCredits: internalTotal?.credits || 0
        }
      })
      .sort((a, b) => b.score - a.score)
      .map((district, index) => ({
        ...district,
        rank: index + 1
      }))
  }, [districtRvmCount, planningYear, machineWithDistrict, internalDistrictTotals, deficiencyFromApi, effectiveDistrictStats])

  const topPriorityDistricts = deficiencyRanking.slice(0, 5)

  const complianceCoverage = useMemo(() => {
    if (deficiencyFromApi?.coverage) {
      return deficiencyFromApi.coverage
    }

    const servedPopulation = deficiencyRanking
      .filter((district) => district.nearestDistance <= 1000)
      .reduce((sum, district) => sum + district.projectedPopulation, 0)

    const totalPopulation = deficiencyRanking.reduce((sum, district) => sum + district.projectedPopulation, 0)
    const value = totalPopulation > 0 ? (servedPopulation / totalPopulation) * 100 : 0

    return {
      percentage: value,
      servedPopulation,
      totalPopulation,
      target: 85
    }
  }, [deficiencyRanking, deficiencyFromApi])

  const toggleLayer = (key: keyof typeof layerState) => {
    setLayerState((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const assessCandidate = (lat: number, lng: number) => {
    const target: [number, number] = [lat, lng]

    const nearestDistance = Math.min(
      ...machineWithDistrict.map((machine) =>
        distanceMeters(target, [machine.latitude, machine.longitude])
      )
    )

    const nearbyRvmCount = machineWithDistrict.filter((machine) => {
      return distanceMeters(target, [machine.latitude, machine.longitude]) <= 1000
    }).length

    const nearbyGovStationCount = effectiveGovStations.filter((station) => {
      return distanceMeters(target, [station.latitude, station.longitude]) <= 1000
    }).length

    const nearestDistrict = effectiveDistrictStats
      .map((district) => ({
        district: district.district,
        distance: distanceMeters(target, district.center),
        population: getProjectedPopulationForYear(district, planningYear),
        sduFactor: district.sduFactor
      }))
      .sort((a, b) => a.distance - b.distance)[0]

    const nearestGovStationMeters = Math.min(
      ...effectiveGovStations.map((station) => distanceMeters(target, [station.latitude, station.longitude]))
    )

    const nearestDistrictMeters = nearestDistrict.distance

    const waterZone = waterExclusionZones.find((zone) => isPointInPolygon(target, zone.polygon))
    const tooRemoteFromAccessCorridor =
      nearestDistrictMeters > 2200 && nearestDistance > 1800 && nearestGovStationMeters > 1800

    const invalidReason = waterZone
      ? `Rejected: point falls inside ${waterZone.name}, which is not a feasible deployment area.`
      : tooRemoteFromAccessCorridor
        ? 'Rejected: point is too far from known urban access corridors or existing service locations.'
        : undefined

    const serviceGap = clamp((nearestDistance / 1000) * 100)
    const facilityScarcity = clamp(100 - nearbyGovStationCount * 20 - nearbyRvmCount * 18)
    const populationWeight = clamp((nearestDistrict.population / 800000) * 100)
    const recyclingGap = clamp(100 - nearbyRvmCount * 22 - nearbyGovStationCount * 12)

    const score = invalidReason
      ? 0
      : Math.round(
          serviceGap * 0.3 +
          facilityScarcity * 0.25 +
          populationWeight * 0.3 +
          recyclingGap * 0.15
        )

    const newCandidate: CandidateSite = {
      id: `CAND-${Date.now()}`,
      latitude: lat,
      longitude: lng,
      nearestRvmMeters: nearestDistance,
      nearbyRvmCount,
      nearbyGovStationCount,
      district: nearestDistrict.district,
      populationWeight: nearestDistrict.population,
      score,
      feasible: !invalidReason,
      invalidReason,
      createdAt: new Date().toISOString()
    }

    setCandidateSites((prev) => [newCandidate, ...prev].slice(0, 20))
  }

  const exportCandidatesCsv = () => {
    if (candidateSites.length === 0) return

    const header = [
      'CandidateID',
      'District',
      'Latitude',
      'Longitude',
      'SuitabilityScore',
      'NearestRvmMeters',
      'NearbyRvmWithin1km',
      'NearbyGovStationsWithin1km',
      'ProjectedPopulation',
      'CreatedAt'
    ]

    const rows = candidateSites.map((site) => [
      site.id,
      site.district,
      site.latitude.toFixed(6),
      site.longitude.toFixed(6),
      site.score,
      Math.round(site.nearestRvmMeters),
      site.nearbyRvmCount,
      site.nearbyGovStationCount,
      site.populationWeight,
      site.createdAt
    ])

    const csvText = [header, ...rows]
      .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
      .join('\n')

    const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `rvm-candidate-sites-${planningYear}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  const printAsPdf = () => {
    window.print()
  }

  const districtTrafficLight = (score: number) => {
    if (score > 35000) return 'text-red-700 bg-red-100'
    if (score > 18000) return 'text-amber-700 bg-amber-100'
    return 'text-green-700 bg-green-100'
  }

  return (
    <div className="py-8 print:py-0 print:bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6 print:max-w-none print:w-full print:px-0 print:space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
            <p className="text-sm text-gray-600 mt-1">
              Planning intelligence for district boundaries, population projections, and candidate-site scoring.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 print:hidden">
            <label className="text-sm font-medium text-gray-700">Projection Year</label>
            <select
              value={planningYear}
              onChange={(event) => setPlanningYear(Number(event.target.value) as PlanningYear)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value={2021}>Current (2021)</option>
              {PROJECTION_YEARS.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <button
              onClick={exportCandidatesCsv}
              disabled={candidateSites.length === 0}
              className="inline-flex items-center px-3 py-2 text-sm rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-gray-300"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
              Export CSV
            </button>
            <button
              onClick={printAsPdf}
              className="inline-flex items-center px-3 py-2 text-sm rounded-md bg-slate-700 text-white hover:bg-slate-800"
            >
              <PrinterIcon className="h-4 w-4 mr-1" />
              Export PDF
            </button>
            <Link to={homeRoute} className="text-sm text-primary-700 hover:text-primary-900 font-medium">
              Back to portal
            </Link>
          </div>
        </div>

        {(apiError || apiStationWarning || districtBoundaryWarning || populationApiError || populationApiData?.warning || populationApiData?.projectionWarning) && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            {apiError && <p>Planning API fallback mode: {apiError}</p>}
            {apiStationWarning && <p>EPD station warning: {apiStationWarning}</p>}
            {districtBoundaryWarning && <p>District boundary warning: {districtBoundaryWarning}</p>}
            {populationApiError && <p>Population data warning: {populationApiError}</p>}
            {populationApiData?.warning && <p>Population fallback warning: {populationApiData.warning}</p>}
            {populationApiData?.projectionWarning && <p>Population projection warning: {populationApiData.projectionWarning}</p>}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 print:block print:space-y-6">
          <section className="bg-white rounded-xl shadow p-4 xl:col-span-1 print:shadow-none print:border print:border-gray-200">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">Layer Controls</h2>
            <div className="space-y-2 text-sm">
              {[
                { key: 'boundaries', label: 'District Boundaries', color: '#334155', type: 'ring', op: 'opacity-70' },
                { key: 'deficiency', label: 'Deficiency Choropleth', color: 'bg-gradient-to-r from-emerald-500 via-amber-400 to-red-600', type: 'gradient' },
                { key: 'population', label: 'Population', color: '#f59e0b', type: 'circle' },
                { key: 'projection', label: 'Projected Population', color: '#9ca3af', type: 'circle' },
                { key: 'rvm', label: 'RVM Machines', color: '#0f766e', type: 'circle' },
                { key: 'govStations', label: 'Gov Recycling Stations', color: '#2563eb', type: 'circle' },
                { key: 'coverage', label: 'Coverage Rings (500m)', color: '#22c55e', type: 'ring', op: 'opacity-20' }
              ].map(({ key, label, color, type, op }) => (
                <label key={key} className="flex items-center gap-3 cursor-pointer py-1">
                  <input
                    type="checkbox"
                    checked={layerState[key as keyof typeof layerState]}
                    onChange={() => toggleLayer(key as keyof typeof layerState)}
                    className="rounded border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                  />
                  
                  {type === 'circle' && (
                    <span className={`inline-block w-3 h-3 rounded-full ${op || ''}`} style={{ backgroundColor: color }} />
                  )}
                  {type === 'dashed-ring' && (
                    <span className="inline-block w-3 h-3 rounded-full border border-dashed" style={{ borderColor: color }} />
                  )}
                  {type === 'ring' && (
                    <span className={`inline-block w-3 h-3 rounded-full border-2 ${op || ''}`} style={{ borderColor: color }} />
                  )}
                  {type === 'gradient' && (
                    <span className={`inline-block w-4 h-3 rounded ${color}`} />
                  )}

                  <span className="text-gray-700 select-none flex-1">{label}</span>
                </label>
              ))}
            </div>

            <div className="mt-6 border-t pt-4 print:hidden">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">PRS Coverage KPI</h3>
              <div className="text-2xl font-bold text-gray-900">{complianceCoverage.percentage.toFixed(1)}%</div>
              <p className="text-xs text-gray-500">Population within 1km of current RVM network</p>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                <div
                  className={`h-2 rounded-full ${complianceCoverage.percentage >= complianceCoverage.target ? 'bg-green-500' : 'bg-amber-500'}`}
                  style={{ width: `${Math.min(complianceCoverage.percentage, 100)}%` }}
                />
              </div>
              <p className="text-xs mt-2 text-gray-600">Target by 2027: {complianceCoverage.target}%</p>
            </div>

            <div className="mt-4 border-t pt-4">
              <p className="text-xs text-gray-500">
                Map Data Source:<br/>
                Common Spatial Data Infrastructure (CSDI), Lands Department, HKSAR
              </p>
              <p className="text-xs text-gray-600 mt-2">
                Population source:{' '}
                <span className="font-semibold">
                  {populationApiData?.source === 'csdi' ? 'CSDI live feed' : 'fallback seed data'}
                </span>
              </p>
              {populationApiData && (
                <>
                  <p className="text-xs text-gray-600 mt-1">
                    Dataset ID: <span className="font-mono">{populationApiData.datasets.current.datasetId}</span>
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    Fetched at: {new Date(populationApiData.fetchedAt).toLocaleString()}
                  </p>
                </>
              )}
            </div>
          </section>

          <section className="bg-white rounded-xl shadow overflow-hidden xl:col-span-3">
            <div className="h-[520px] print:h-[600px] print:border print:border-gray-200 print:rounded-lg overflow-hidden">
              <CSDIPlanningMap
                layerState={layerState}
                machineWithDistrict={machineWithDistrict}
                effectiveGovStations={effectiveGovStations}
                planningYear={planningYear}
                utilizationLookup={utilizationLookup}
                districtMetrics={deficiencyRanking}
                districtPopulationStats={effectiveDistrictStats}
                candidateSites={candidateSites}
                onBoundarySourceChange={setDistrictBoundarySource}
                onBoundaryWarningChange={setDistrictBoundaryWarning}
                onMapClick={assessCandidate}
              />
            </div>
          </section>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 print:block print:mt-4 print:space-y-6">
          <section className="bg-white rounded-xl shadow p-4 xl:col-span-2 print:shadow-none print:border print:border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900 flex items-center">
                <GlobeAsiaAustraliaIcon className="h-5 w-5 mr-2 text-primary-600" />
                District RVM Deficiency Ranking
              </h2>
              <span className="text-xs text-gray-500">
                {deficiencyFromApi
                  ? 'Computed by planning API: (Projected population / RVM count) * (1 + SDU factor) * gap factor'
                  : 'Formula: (Projected population / RVM count) * (1 + SDU factor) * gap factor'}
              </span>
            </div>

            <div className="overflow-hidden print:overflow-visible">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b print:break-inside-avoid">
                    <th className="py-2 pr-3">Rank</th>
                    <th className="py-2 pr-3">District</th>
                    <th className="py-2 pr-3">Current Pop</th>
                    <th className="py-2 pr-3">Projected Pop</th>
                    <th className="py-2 pr-3">RVM</th>
                    <th className="py-2 pr-3">Score</th>
                    <th className="py-2 pr-3">Trend</th>
                    <th className="py-2">Sparkline</th>
                  </tr>
                </thead>
                <tbody>
                  {deficiencyRanking.map((district) => {
                    const sparkline = getSparklinePoints(district.trendSeries || [])
                    const trendArrow = getTrendArrow(district.trendDelta || 0)
                    const trendClass = district.trendDelta && district.trendDelta > 0
                      ? 'text-red-700 bg-red-100'
                      : district.trendDelta && district.trendDelta < 0
                        ? 'text-emerald-700 bg-emerald-100'
                        : 'text-slate-700 bg-slate-100'

                    return (
                      <tr key={district.district} className="border-b last:border-b-0 print:break-inside-avoid">
                        <td className="py-2 pr-3 font-semibold text-gray-800">#{district.rank}</td>
                        <td className="py-2 pr-3 font-medium text-gray-800">{district.district}</td>
                        <td className="py-2 pr-3">{district.currentPopulation.toLocaleString()}</td>
                        <td className="py-2 pr-3">{district.projectedPopulation.toLocaleString()}</td>
                        <td className="py-2 pr-3">{district.rvmCount}</td>
                        <td className="py-2 pr-3">
                          <span className={`text-xs px-2 py-1 rounded-full ${districtTrafficLight(district.score)}`}>
                            {district.score.toFixed(0)}
                          </span>
                        </td>
                        <td className="py-2 pr-3">
                          <span className={`text-xs px-2 py-1 rounded-full ${trendClass}`}>
                            {trendArrow}
                          </span>
                        </td>
                        <td className="py-2">
                          <svg viewBox="0 0 120 28" className="h-7 w-28">
                            <path d={sparkline.path} fill="none" stroke="#0f766e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            {sparkline.scaledPoints.length > 0 && (
                              <circle
                                cx={sparkline.scaledPoints[sparkline.scaledPoints.length - 1].x}
                                cy={sparkline.scaledPoints[sparkline.scaledPoints.length - 1].y}
                                r="2.5"
                                fill="#0f766e"
                              />
                            )}
                          </svg>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <section className="bg-white rounded-xl shadow p-4 print:shadow-none print:border print:border-gray-200 print:break-inside-avoid print:mt-6">
            <h2 className="text-base font-semibold text-gray-900 flex items-center mb-4">
              <SparklesIcon className="h-5 w-5 mr-2 text-emerald-600" />
              Top 5 Priority Districts
            </h2>
            <div className="space-y-3">
              {topPriorityDistricts.map((district) => {
                const sparkline = getSparklinePoints(district.trendSeries || [])
                const trendArrow = getTrendArrow(district.trendDelta || 0)

                return (
                  <div key={district.district} className="rounded-lg bg-gray-50 p-3">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-gray-800">#{district.rank} {district.district}</p>
                      <p className="text-sm text-red-700 font-semibold">{district.score.toFixed(0)}</p>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Current {district.currentPopulation.toLocaleString()} | Projected {district.projectedPopulation.toLocaleString()} | RVM {district.rvmCount}
                    </p>
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${district.trendDelta && district.trendDelta > 0 ? 'text-red-700 bg-red-100' : district.trendDelta && district.trendDelta < 0 ? 'text-emerald-700 bg-emerald-100' : 'text-slate-700 bg-slate-100'}`}>
                        {trendArrow} {district.trendDelta ? Math.abs(district.trendDelta).toFixed(0) : '0'}
                      </span>
                      <svg viewBox="0 0 120 28" className="h-7 w-28">
                        <path d={sparkline.path} fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        {sparkline.scaledPoints.length > 0 && (
                          <circle
                            cx={sparkline.scaledPoints[sparkline.scaledPoints.length - 1].x}
                            cy={sparkline.scaledPoints[sparkline.scaledPoints.length - 1].y}
                            r="2.5"
                            fill="#7c3aed"
                          />
                        )}
                      </svg>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="mt-6 border-t pt-4 print:hidden">
              <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                <MapPinIcon className="h-4 w-4 mr-1 text-violet-600" />
                Candidate Site Assessments
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                Click on the map to score a candidate site. Higher score means a larger service gap on feasible land locations; open-water or remote off-land clicks are rejected.
              </p>
              <div className="space-y-2 max-h-56 overflow-auto">
                {candidateSites.length === 0 && (
                  <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
                    No candidate selected yet.
                  </div>
                )}
                {candidateSites.map((site) => (
                  <div key={site.id} className="text-xs rounded-lg border border-gray-200 p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-800">{site.id}</span>
                      <span className={`font-bold ${site.feasible ? 'text-violet-700' : 'text-red-700'}`}>
                        {site.feasible ? `Score ${site.score}` : 'Rejected'}
                      </span>
                    </div>
                    <p className="mt-1 text-gray-600">{site.district}</p>
                    <p className="text-gray-500">Nearest RVM: {Math.round(site.nearestRvmMeters)}m</p>
                    <p className="text-gray-500">Nearby RVM / Gov: {site.nearbyRvmCount} / {site.nearbyGovStationCount}</p>
                    {!site.feasible && site.invalidReason && (
                      <p className="text-red-700 mt-1">{site.invalidReason}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>

        <section className="bg-white rounded-xl shadow p-4 print:shadow-none print:border print:border-gray-200 print:break-inside-avoid print:mt-6">
          <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
            <BuildingOffice2Icon className="h-5 w-5 mr-2 text-slate-600" />
            Planning Data Sources & Utilisation Summary
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="rounded-lg bg-red-50 p-4">
              <div className="flex items-center mb-1 text-red-700 font-semibold">
                <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                High Demand Machines
              </div>
              <p className="text-2xl font-bold text-red-700">{utilizationByMachine.filter((item) => item.highDemand).length}</p>
              <p className="text-xs text-red-700/80">Priority for 2nd unit at same site</p>
            </div>
            <div className="rounded-lg bg-amber-50 p-4">
              <div className="font-semibold text-amber-700 mb-1">Low Utilization Machines</div>
              <p className="text-2xl font-bold text-amber-700">{utilizationByMachine.filter((item) => item.lowUtilization).length}</p>
              <p className="text-xs text-amber-700/80">Candidate for relocation review</p>
            </div>
            <div className="rounded-lg bg-emerald-50 p-4">
              <div className="font-semibold text-emerald-700 mb-1">Role Access</div>
              <p className="text-sm text-emerald-800">
                {readonlyView
                  ? 'Read-only mode enabled for retailer planning review.'
                  : 'Full planning mode for government regulators and system administrators.'}
              </p>
            </div>
          </div>

          <div className="mt-4 text-xs text-gray-500 border-t pt-3 space-y-1">
            <p>
              Population layer source is{' '}
              <span className="font-semibold">
                {populationApiData?.source === 'csdi' ? 'CSDI Population FeatureServer' : 'fallback seed data'}
              </span>
              {populationApiData && (
                <>
                  {' '}
                  (dataset <span className="font-mono">{populationApiData.datasets.current.datasetId}</span>, fetched{' '}
                  {new Date(populationApiData.fetchedAt).toLocaleString()})
                </>
              )}
              .
            </p>
            <p>
              Population projection source is{' '}
              <span className="font-semibold">
                {populationApiData?.source === 'csdi' && !populationApiData.projectionWarning
                  ? 'CSDI projection FeatureServer'
                  : 'fallback or interpolated projections'}
              </span>
              {populationApiData?.source === 'csdi' && (
                <>
                  {' '}
                  (dataset <span className="font-mono">{populationApiData.datasets.projections.datasetId}</span>)
                </>
              )}
              .
            </p>
            <p>
              Boundary layer source is <span className="font-semibold">{districtBoundarySource === 'csdi' ? 'CSDI district boundary WFS' : 'fallback boundary geometry'}</span>.
            </p>
            <p>
              Government station layer source is <span className="font-semibold">{apiStationSource === 'csdi' ? 'CSDI live feed' : 'fallback seed data'}</span>.
            </p>
            <p>
              RVM utilisation is encoded on the markers and surfaced in the popup instead of a standalone layer.
            </p>
          </div>

          {csdiReferences.length > 0 && (
            <div className="mt-3 text-xs text-gray-600">
              <p className="font-semibold text-gray-700 mb-2">Recommended CSDI reference layers</p>
              <ul className="space-y-1 list-disc list-inside">
                {csdiReferences.map((item) => (
                  <li key={item.name}>
                    <span className="font-medium">{item.name}</span> ({item.recommendedLayerType}) - {item.purpose}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}

export default RvmPlanningPage
